import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../components/ThemeContext';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, Shield, Copy, ChevronDown, Circle } from 'lucide-react';
import AxonMetricHeader from '../../components/layout/AxonMetricHeader.jsx';
import apiService from '../../services/api';
import backendApi from '../../services/backendApi';

// Enhanced Floating Time and Date Widget
const FloatingTimeDate = () => {
  const [time, setTime] = useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-20 right-6 z-30 bg-gradient-to-br from-[#0084CF] via-[#0066A3] to-[#0066A3] backdrop-blur-md border border-white/20 rounded-xl shadow-xl shadow-[#0084CF]/30 px-4 py-3"
    >
      <div className="text-right text-white">
        <div className="text-sm font-bold">
          {time.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: true 
          })}
        </div>
        <div className="text-xs font-medium opacity-90">
          {time.toLocaleDateString('en-US', { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric'
          })}
        </div>
      </div>
    </motion.div>
  );
};

const ForgotPasswordPage = () => {
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedSiteData, setSelectedSiteData] = useState(null);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [resetUrl, setResetUrl] = useState(null);
  const [message, setMessage] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  // Fetch sites from database
  const fetchSites = useCallback(async () => {
    try {
      const response = await backendApi.getSites(false); // Get all sites
      if (response.success && response.sites) {
        setSites(response.sites);
      } else {
        console.error('Failed to load sites:', response.error);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoadingSites(false);
    }
  }, []);

  // Initial fetch sites
  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.site-dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous states
    setError('');
    setMessage('');
    setResetToken(null);
    setResetUrl(null);
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    // Enhanced email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate site selection
    if (!selectedSiteData && !selectedSite) {
      setError('Please select a site');
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      // Prepare site information
      const siteId = selectedSiteData?.site_id || null;
      const siteName = selectedSiteData?.site_name || null;
      
      const response = await apiService.forgotPassword(email.trim(), siteId, siteName);
      
      if (response.success) {
        setSuccess(true);
        setMessage(response.message || 'If an account with that email exists for the selected site, a password reset link has been sent.');
        
        // In development mode, store the reset token and URL for display
        if (response.resetToken) {
          setResetToken(response.resetToken);
        }
        if (response.resetUrl) {
          setResetUrl(response.resetUrl);
        } else if (response.resetToken && selectedSiteData) {
          // Build reset URL if not provided but we have token and site
          const siteId = selectedSiteData.site_id;
          const builtUrl = `http://localhost:3000/reset-password?token=${response.resetToken}&site_id=${siteId}`;
          setResetUrl(builtUrl);
        }
      } else {
        // Handle API errors
        const errorMessage = response.error || response.message || 'Failed to send reset link. Please try again.';
        setError(errorMessage);
        setSuccess(false);
        
        // Log specific error types for debugging
        if (response.error === 'EMAIL_NOT_FOUND_FOR_SITE') {
}
      }
    } catch (err) {
      // Handle network errors and other exceptions
      console.error('Forgot password error:', err);
      
      if (err.message && err.message.includes('Failed to fetch')) {
        setError('Unable to connect to the server. Please check your connection and ensure the backend server is running.');
      } else if (err.message && err.message.includes('NetworkError')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'An unexpected error occurred. Please try again later.');
      }
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSiteSelect = (site) => {
    // Prevent selection of inactive sites
    if (!site.is_active) {
      setError("Cannot select an inactive site. Please choose an active site.");
      return;
    }
    
    setSelectedSiteData(site);
    setSelectedSite(site.site_id || site.site_code);
    setIsDropdownOpen(false);
    setError(""); // Clear any previous errors
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => {
        setCopiedField(null);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard. Please copy manually.');
    });
  };

  return (
    <div style={{
      backgroundImage: "url('/assets/login_bg.jpg')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      minHeight: "100vh",
      position: "relative"
    }}>
      {isDarkMode && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(11, 17, 32, 0.50)",
          zIndex: 0,
          pointerEvents: "none"
        }} />
      )}
      {/* Enhanced CSS Styles with Professional Gradients */}
      <style>{`
        .glass-morphism {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .brand-glass {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.25) 0%, 
            rgba(255, 255, 255, 0.15) 50%, 
            rgba(0, 132, 207, 0.1) 100%
          );
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .floating-animation {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .gradient-border {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 25%, 
            #1C4D88 50%, 
            #0084CF 75%, 
            #0084CF 100%
          );
          background-size: 300% 300%;
          animation: gradientShift 8s ease infinite;
          padding: 2px;
          border-radius: 20px;
          backdrop-filter: blur(20px);
          box-shadow: 0 25px 50px -12px rgba(0, 132, 207, 0.25);
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .inner-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(30px);
          border-radius: 18px;
        }

        .background-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-image: url('/assets/login_bg.jpg');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          z-index: -2;
        }

        .background-overlay {
          display: none;
        }
        
        @keyframes overlayShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .professional-button {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 50%, 
            #1C4D88 100%
          );
          transition: all 0.3s ease;
        }

        .professional-button:hover {
          background: linear-gradient(135deg, 
            #0066A3 0%, 
            #0066A3 50%, 
            #68d391 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(0, 132, 207, 0.4);
        }
      `}</style>

      {/* Keep Original Background Image */}
      <div className="background-container"></div>
      <div className="background-overlay"></div>

      <div className="min-h-screen w-full flex flex-col relative" style={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <AxonMetricHeader />

        {/* Enhanced Floating Time and Date */}
        <FloatingTimeDate />

        {/* Main Content - Adjusted for proper spacing */}
        <main className="relative z-10 flex-1 w-full flex items-center justify-center px-4 py-6" style={{minHeight: 'calc(100vh - 50px)', paddingTop: '80px'}}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full max-w-5xl mx-auto"
          >
            <div className="gradient-border">
              <div className="inner-content flex rounded-2xl overflow-hidden shadow-2xl">
                
                {/* Left Panel - Enhanced with Professional Gradients */}
                <section className="hidden md:flex flex-col justify-center w-2/5 p-6 relative overflow-hidden">
                  {/* Beautiful gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F2F8FC]/90 via-[#F2F8FC]/80 to-[#F2F8FC]/70"></div>
                  
                  {/* Enhanced background elements with gradients */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#0084CF]/20 via-[#3EA3DA]/15 to-[#3EA3DA]/10 rounded-full blur-2xl floating-animation"></div>
                  <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-br from-[#C2DFF1]/30 via-[#C2DFF1]/25 to-[#0084CF]/15 rounded-full blur-2xl"></div>
                  
                  <div className="relative z-10">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                      className="mb-8"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-gradient-to-br from-[#0084CF]/20 via-[#C2DFF1]/40 to-[#C2DFF1]/30 rounded-xl shadow-sm border border-[#0084CF]/20 backdrop-blur-sm">
                          <Shield size={24} className="text-[#0084CF]" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold bg-gradient-to-r from-[#0084CF] via-[#0066A3] to-[#0066A3] bg-clip-text text-transparent">
                            Password Recovery
                          </h2>
                          <p className="text-[#1C4D88] font-medium">Secure Reset Portal</p>
                        </div>
                      </div>
                    </motion.div>

                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      className="text-base leading-relaxed mb-8 text-gray-900 font-extrabold"
                    >
                      Enter your email address and select your site to receive a password reset link. The reset link will be valid for 10 minutes. Site selection is required to ensure the correct account is reset.
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                      className="space-y-5"
                    >
                      {[
                        "Secure password reset process",
                        "Email verification required",
                        "Quick and easy recovery"
                      ].map((feature, index) => (
                        <motion.div
                          key={feature}
                          initial={{ opacity: 0, x: -15 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
                          className="flex items-center space-x-4 group"
                        >
                          <div className="w-7 h-7 bg-gradient-to-r from-[#0084CF]/30 via-[#8AC4E6]/40 to-[#8AC4E6]/30 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm border border-[#0084CF]/30">
                            <CheckCircle size={14} className="text-[#0084CF]" />
                          </div>
                          <span className="text-gray-900 font-extrabold group-hover:text-[#0084CF] transition-colors duration-300">{feature}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>
                </section>

                {/* Right Panel - Keep Original Size */}
                <section className="w-full md:w-3/5 p-6 flex flex-col justify-center relative">
                  <div className="w-full max-w-md mx-auto">
                    {success ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        className="space-y-6"
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1, duration: 0.5 }}
                          className="mb-6 text-center"
                        >
                          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent mb-2">
                            Check Your Email
                          </h1>
                          <p className="text-gray-900 font-extrabold">
                            Reset link sent successfully
                          </p>
                        </motion.div>

                        <div className="bg-green-50/95 backdrop-blur-sm border border-green-200 rounded-xl p-6 text-center">
                          <div className="flex justify-center mb-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                              <CheckCircle size={32} className="text-white" />
                            </div>
                          </div>
                          <h3 className="text-lg font-bold text-green-800 mb-2">
                            Reset Link Sent!
                          </h3>
                          <p className="text-sm text-green-700 mb-4">
                            {message || 'If an account with that email exists, a password reset link has been sent.'}
                          </p>
                          {email && (
                            <p className="text-xs text-green-600 mb-4">
                              Email: <strong>{email}</strong>
                            </p>
                          )}
                          <p className="text-xs text-green-600">
                            Please check your inbox and follow the instructions to reset your password.
                          </p>
                        </div>

                        {/* Development Mode: Show Reset Token and URL */}
                        {(resetToken || resetUrl) && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                            className="bg-[#F2F8FC]/95 backdrop-blur-sm border border-[#C2DFF1] rounded-xl p-4"
                          >
                            <h4 className="text-sm font-bold text-[#1C4D88] mb-3 text-center">
                              Development Mode - Reset Details
                            </h4>
                            
                            {resetUrl && (
                              <div className="mb-3">
                                <label className="block text-xs font-semibold text-[#1C4D88] mb-1">
                                  Reset URL:
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    readOnly
                                    value={resetUrl}
                                    className="flex-1 px-3 py-2 text-xs bg-white border border-[#8AC4E6] rounded-lg text-gray-900 font-mono"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(resetUrl, 'url')}
                                    className="px-3 py-2 bg-[#0066A3] text-white text-xs font-semibold rounded-lg hover:bg-[#1C4D88] transition-colors flex items-center gap-1"
                                  >
                                    <Copy size={14} />
                                    {copiedField === 'url' ? 'Copied!' : 'Copy'}
                                  </button>
                                </div>
                                {(() => {
                                  try {
                                    const url = new URL(resetUrl);
                                    const path = url.pathname + url.search;
                                    return (
                                      <Link
                                        to={path}
                                        className="block mt-2 text-xs text-[#0066A3] hover:text-[#1C4D88] font-semibold text-center"
                                      >
                                        Click here to reset password →
                                      </Link>
                                    );
                                  } catch {
                                    // Extract site_id from resetUrl if available
                                    const urlParams = new URLSearchParams(resetUrl.split('?')[1] || '');
                                    const siteIdFromUrl = urlParams.get('site_id');
                                    const resetPath = siteIdFromUrl 
                                      ? `/reset-password?token=${resetToken}&site_id=${siteIdFromUrl}`
                                      : `/reset-password?token=${resetToken}`;
                                    
                                    return (
                                      <Link
                                        to={resetPath}
                                        className="block mt-2 text-xs text-[#0066A3] hover:text-[#1C4D88] font-semibold text-center"
                                      >
                                        Click here to reset password →
                                      </Link>
                                    );
                                  }
                                })()}
                              </div>
                            )}
                            
                            {resetToken && (
                              <div>
                                <label className="block text-xs font-semibold text-[#1C4D88] mb-1">
                                  Reset Token (for testing):
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    readOnly
                                    value={resetToken}
                                    className="flex-1 px-3 py-2 text-xs bg-white border border-[#8AC4E6] rounded-lg text-gray-900 font-mono"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(resetToken, 'token')}
                                    className="px-3 py-2 bg-[#0066A3] text-white text-xs font-semibold rounded-lg hover:bg-[#1C4D88] transition-colors flex items-center gap-1"
                                  >
                                    <Copy size={14} />
                                    {copiedField === 'token' ? 'Copied!' : 'Copy'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}

                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.3, duration: 0.5 }}
                          className="space-y-3"
                        >
                          <Link
                            to="/login"
                            className="block w-full py-4 rounded-xl font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center shadow-xl professional-button text-white shadow-[#0084CF]/40 hover:shadow-[#0084CF]/60 hover:shadow-2xl"
                          >
                            <ArrowLeft size={18} className="mr-2" />
                            <span>Back to Login</span>
                          </Link>
                          <button
                            onClick={() => {
                              setSuccess(false);
                              setEmail('');
                              setResetToken(null);
                              setResetUrl(null);
                              setMessage('');
                            }}
                            className="block w-full py-3 px-4 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                          >
                            Request Another Reset Link
                          </button>
                        </motion.div>
                      </motion.div>
                    ) : (
                      <>
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1, duration: 0.5 }}
                          className="mb-6 text-center"
                        >
                          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent mb-2">
                            Forgot Password
                          </h1>
                          <p className="text-gray-900 font-extrabold">
                            Enter your email and select your site to receive a password reset link
                          </p>
                        </motion.div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                          <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                          >
                            <label className="block text-sm font-extrabold mb-2 text-gray-900 flex items-center gap-2">
                              <Mail size={16} className="text-[#0084CF]" />
                              Email Address
                            </label>
                            <div className="relative">
                              <input
                                type="email"
                                placeholder="Enter your email address"
                                className="w-full p-3 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all duration-300 font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-black placeholder-black/70"
                                style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.9)', fontSize: 'calc(1rem + 2px)' }}
                                value={email}
                                onChange={(e) => {
                                  setEmail(e.target.value);
                                  setError('');
                                }}
                                disabled={loading}
                                autoComplete="email"
                              />
                            </div>
                          </motion.div>

                          {/* Site Selection Field */}
                          <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25, duration: 0.5 }}
                          >
                            <label className="block text-sm font-extrabold mb-2 text-gray-900 flex items-center gap-2">
                              <Shield size={16} className="text-[#0084CF]" />
                              Site
                            </label>
                            <div className="relative site-dropdown-container">
                              <button
                                type="button"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                disabled={loading || loadingSites}
                                className="w-full p-3 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all duration-300 font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-black flex items-center justify-between"
                                style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.9)', fontSize: 'calc(1rem + 2px)' }}
                              >
                                <span className={`font-bold ${selectedSiteData ? 'text-black' : 'text-black/70'}`}>
                                  {selectedSiteData ? selectedSiteData.site_name : 'Select a site'}
                                </span>
                                <ChevronDown 
                                  size={18} 
                                  className={`text-[#0084CF] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                                />
                              </button>
                              
                              {isDropdownOpen && (
                                <div className="absolute z-30 w-full mt-3 rounded-xl shadow-2xl max-h-60 overflow-y-auto brand-glass border border-[#0084CF]/30 backdrop-blur-xl">
                                  {loadingSites ? (
                                    <div className="w-full px-5 py-4 text-center text-gray-900 font-extrabold">
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin"></div>
                                        <span>Loading sites...</span>
                                      </div>
                                    </div>
                                  ) : sites.length === 0 ? (
                                    <div className="w-full px-5 py-4 text-center text-gray-900 font-extrabold">
                                      No sites available
                                    </div>
                                  ) : (
                                    [...sites].sort((a, b) => {
                                      if (a.is_active === b.is_active) return 0;
                                      return a.is_active ? -1 : 1;
                                    }).map((site) => (
                                      <button
                                        key={site.site_id}
                                        type="button"
                                        onClick={(e) => {
                                          if (!site.is_active) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            return;
                                          }
                                          handleSiteSelect(site);
                                        }}
                                        disabled={!site.is_active}
                                        aria-disabled={!site.is_active}
                                        className={`w-full px-5 py-4 text-left border-b last:border-b-0 border-[#0084CF]/20 first:rounded-t-xl last:rounded-b-xl group transition-all ${
                                          site.is_active 
                                            ? 'hover:bg-gradient-to-r hover:from-[#0084CF]/20 hover:to-[#E3F0F9]/30 cursor-pointer bg-white/50' 
                                            : 'opacity-75 cursor-not-allowed bg-gray-100/80 hover:bg-gray-100/80'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className={`font-extrabold ${site.is_active ? 'text-gray-900 group-hover:text-[#0084CF]' : 'text-gray-900'}`}>
                                            {site.site_name}
                                          </div>
                                          <div className="flex items-center gap-2">
                                            {site.is_active ? (
                                              <div className="flex items-center gap-1 text-green-600">
                                                <Circle size={8} className="fill-green-500 text-green-500" />
                                                <span className="text-xs font-medium">Active</span>
                                              </div>
                                            ) : (
                                              <div className="flex items-center gap-1 text-red-600">
                                                <Circle size={8} className="fill-red-500 text-red-500" />
                                                <span className="text-xs font-medium">Inactive</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {site.site_code && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            Code: {site.site_code}
                                          </div>
                                        )}
                                      </button>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </motion.div>
                          
                          {error && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-red-50/95 backdrop-blur-sm border border-red-200 rounded-xl p-4"
                            >
                              <p className="text-sm text-red-700 font-extrabold text-center flex items-center justify-center gap-3">
                                <span className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">!</span>
                                </span>
                                {error}
                              </p>
                            </motion.div>
                          )}

                          <motion.button
                            type="submit"
                            disabled={loading || !email}
                            whileHover={{ scale: loading ? 1 : 1.02 }}
                            whileTap={{ scale: loading ? 1 : 0.98 }}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                            className={`w-full py-3 mt-6 rounded-xl font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center shadow-xl ${
                              loading || !email
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                                : "professional-button text-white shadow-[#0084CF]/40 hover:shadow-[#0084CF]/60 hover:shadow-2xl"
                            }`}
                          >
                            {loading ? (
                              <div className="flex items-center gap-4">
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Sending...</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <Mail size={18} />
                                <span>Send Reset Link</span>
                              </div>
                            )}
                          </motion.button>

                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                            className="text-center pt-4"
                          >
                            <Link
                              to="/login"
                              className="flex items-center justify-center gap-2 text-[#0084CF] hover:text-[#0066A3] font-extrabold transition-colors"
                            >
                              <ArrowLeft size={18} />
                              <span>Back to Login</span>
                            </Link>
                          </motion.div>
                        </form>
                      </>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;

